import DLabGrid from "../../../components/DLabGrid/DLabGrid";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DropBookingMeta, singleEventSkeleton } from "../../../constants";
import BackdropPortal from "../../../components/shared/BackdropPortal";
import {
  filterParams,
  getWeekStartAndEndInISOString,
  onlyUnique,
  replaceOffsetWithOther,
  valueOrEmpty,
  formatUserDisplay,
  USER_FORMAT_PRESETS,
  getBookedUserDisplay,
  getBookingForUsers,
} from "../../../utils/helpers/text/index";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { PaginationContext } from "../../../components/shared/pagination/PaginationContext";
import { getAllData } from "../../../utils/helpers/fetching";
import { GET_BOOKING_DATA_BY_SITE, GET_BOOKING_ENTRIES_BY_BOOKING_ID } from "../../../gql/bookingapi";
import { APPROVE_BOOKING } from "../../../gql/bookingapi/mutations";
import { compose, withApollo } from "react-apollo";
import { connect, useSelector } from "react-redux";
import { loadPendingReservations as loadPendingReservationsAction } from "../../booking-calendars/redux/actions";
import BookingMenuAction from "./BookingMenuAction";
import moment from "moment/moment";
import BookingDetailRenderer from "../../../components/shared/BookingDetailRenderer";
import { useHistory } from "react-router-dom";
import { BookingContext } from "../../booking/context";
import { cloneDeep } from "lodash";
import { BookingTypeChip } from "../../../components/shared/BookingTypeChip";
import { BookingHazardousChip } from "../../../components/shared/BookingHazardousChip";
import { EN_DASH } from "@digitallab/grid-common-frontend-components";
import { getBookingDisplayText } from "../../../components/shared/helpFunctions";
import { hazardousStatusGetter } from "../../../utils/helpers/data";
import {
  createDefaultCsvExportParams,
  createDefaultExcelExportParams,
  createClipboardCellProcessor,
  getExportableColumnKeys,
} from "../../../utils/helpers/exportValueMapper";
import BookingsPaginationActions from "../../../components/shared/BookingsPaginationActions";
import { statusGetter } from "./Utils";

const BookingList = ({
  client,
  resetAll,
  searchQuery,
  setSearchQuery,
  setSelectedDateRange,
  selectedDateRange,
  setDropType,
  setIsDropBooking,
  setIsDialogOpen,
  showAdvanceFilter,
  setSelectedEquipment,
  selectedEquipment,
  setOpenMultiDeleteDialog,
  setOpenRejectBookingDialog,
  setRowEquipmentId,
  tempRowData,
  setTempRowData,
  setDisableResetAll,
  setResetAll,
  loadPendingReservations,
  setPopupEnableStatus,
  setIsRejectBooking,
  setIsRejectDialogOpen,
  setRejectRowEquipmentId,
  // Hazardous rejection props
  setOpenHazardousRejectDialog,
  setHazardousBookingToReject,
  // Assistance assignment props
  setOpenAssistanceDialog,
  setBookingForAssistance,
  setIsEditMode,
  status,
  setStatus,
  // URL action context
  urlAction = null,
  bookingIdFilter = null,
  onGridApiReady = null,
}) => {
  const { nextToken, data, nextData, fetching, dispatchAction } = useContext(PaginationContext);
  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);
  const user = useSelector((store) => store.user);
  const users = useSelector((state) => state.users.users);

  const [gridApi, setGridApi] = useState(null);
  const isFetchingRef = useRef(false);
  const [rowPerPage, setRowPerPage] = useState(10);

  const gridRef = useRef();
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };
  const history = useHistory();

  // Track previous bookingIdFilter to detect changes
  const prevBookingIdFilter = useRef(undefined);

  useEffect(() => {
    const bookingIdFilterChanged = prevBookingIdFilter.current !== bookingIdFilter;
    const isInitialLoad = prevBookingIdFilter.current === undefined;

    prevBookingIdFilter.current = bookingIdFilter;

    // Fetch data when:
    // 1. bookingIdFilter changes (including from null to value or value to null)
    // 2. Date range or nextData changes (only when no bookingIdFilter)
    if (!fetching && !isFetchingRef.current) {
      if (bookingIdFilterChanged && bookingIdFilter !== null) {
        // Fetch with booking ID filter
        fetchData().then(() => {
          setResetAll(false);
        });
      } else if (bookingIdFilter === null && !isInitialLoad) {
        // Normal fetch with date range when no booking filter (but not on initial load as it's already handled)
        fetchData().then(() => {
          setResetAll(false);
        });
      } else if (isInitialLoad && bookingIdFilter === null) {
        // Initial load with no filter
        fetchData().then(() => {
          setResetAll(false);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange, nextData, bookingIdFilter]);

  useEffect(() => {
    if (gridApi) {
      gridRef.current?.api?.setQuickFilter(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const dateFromFormatter = (params) => {
    return moment(params?.data?.dateFrom).tz(user.timezone).format("DD-MMM-YYYY");
  };
  const dateToFormatter = (params) => {
    return moment(params?.data?.dateTo).tz(user.timezone).format("DD-MMM-YYYY");
  };
  const equipmentGetter = (params) => {
    const equipment = params?.data?.bookedEquipments ?? [];
    if (!equipment.length) {
      return "-";
    }
    const result = equipment[0] && equipment[0].length > 10 ? equipment[0].substring(0, 10) + "... " : equipment[0];
    const suffix = equipment.length - 1 > 0 ? `+${equipment.length - 1}` : "";
    return result + suffix;
  };
  const reservedForGetter = (params) => {
    const users = getBookingForUsers(params?.data);
    if (!users.length) {
      return "-";
    }
    const display = getBookedUserDisplay(users[0]);
    const result = display.length > 10 ? display.substring(0, 10) + "... " : display;
    const suffix = users.length - 1 > 0 ? ` +${users.length - 1}` : "";
    return result + suffix;
  };
  const reservedForFilterFormatterGetter = (params) => {
    const users = getBookingForUsers(params?.data);
    if (!users.length) {
      return "-";
    }
    return users.map(getBookedUserDisplay);
  };

  const remarkGetter = (params) => {
    return valueOrEmpty(params?.data?.remark, false, "-");
  };

  const fetchData = async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    // Set the fetching flag
    isFetchingRef.current = true;

    // Don't trigger additional loading if hazardous operation is in progress
    if (!window.hazardousApprovalActive) {
      dispatchAction({
        type: "fetching",
        payload: true,
      });
    }

    let dateFrom;
    let dateTo;
    if (selectedDateRange[0] instanceof Date) {
      const dateFromString = moment(selectedDateRange[0]).format();
      const dateToString = moment(selectedDateRange[1]).format();
      const offset = moment.tz(user.timezone).format("Z");
      dateFrom = moment(replaceOffsetWithOther(dateFromString, offset)).utc().format();
      dateTo = moment(replaceOffsetWithOther(dateToString, offset, true))
        .utc()
        .format();
    } else {
      dateFrom = moment(selectedDateRange[0]).utc().format();
      dateTo = moment(selectedDateRange[1]).utc().format();
    }

    // If bookingIdFilter is set, use it to filter by specific booking ID
    // Otherwise use the date range filter
    const bookingFilter = bookingIdFilter
      ? {
          and: [{ id: { eq: bookingIdFilter } }],
        }
      : {
          and: [
            {
              and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }],
            },
          ],
        };

    const { nextToken: token, items } = await getAllData({
      client,
      query: GET_BOOKING_DATA_BY_SITE,
      dataPath: ["data", "getBookingDataBySite"],
      drillData: true,
      variables: {
        filter: JSON.stringify(bookingFilter),
        dateFrom: dateTo,
        site: user.site,
        limit: 1000,
        nextToken,
      },
      cache: true,
      cacheTTLSeconds: 60,
    });

    dispatchAction({
      type: "nextToken",
      payload: {
        token,
        data: items,
      },
    });
    setTempRowData(items);

    // Clear the fetching flag after successful fetch
    isFetchingRef.current = false;

    return { nextToken, items };
  };

  const isRowSelectable = useMemo(() => {
    return (params) => {
      const node = params.level;
      const isGroupRow = node === 0;
      const isActive = params.data.active === true;
      if (isGroupRow) {
        params.selectable = isActive;
      }
      return isGroupRow && isActive;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const refreshGrid = useCallback(() => {
    if (gridApi && gridRef.current) {
      gridRef.current.api.refreshCells();
      console.log("Admin grid refreshed after approval action");
    }
  }, [gridApi]);

  const handleApprove = useCallback(
    async (booking) => {
      // Check if booking requires assistance
      if (booking?.assistanceDeclaration?.requiresAssistance) {
        // Open assistance assignment dialog
        if (setBookingForAssistance && setOpenAssistanceDialog && setIsEditMode) {
          setBookingForAssistance(booking);
          setIsEditMode(false); // Ensure we're in approval mode, not edit mode
          setOpenAssistanceDialog(true);
        }
      } else {
        // Direct approval for non-assistance bookings
        try {
          dispatchAction({
            type: "fetching",
            payload: true,
          });

          await client.mutate({
            mutation: APPROVE_BOOKING,
            variables: {
              bookingId: booking.id,
              approval: {
                status: "APPROVED",
                approvedBy: user.user,
                approvedByEmail: user.email,
              },
              createdBy: user.user,
              createdByEmail: user.email,
            },
          });

          // Don't show success here - let the subscription callback handle it
          // The success message will come from bookingCallbackFactory when it receives APPROVED_SUCCEEDED
        } catch (error) {
          console.error("Approval error:", error);
          snackbarService.show({
            type: "error",
            message: "Failed to approve booking",
            duration: 3000,
          });
          setStatus(false);
          dispatchAction({
            type: "clearAll",
          });
        }
      }
    },
    [setBookingForAssistance, setOpenAssistanceDialog, setIsEditMode, client, user, setStatus, dispatchAction],
  );

  const handleReject = useCallback(
    (booking) => {
      if (setHazardousBookingToReject && setOpenHazardousRejectDialog) {
        setHazardousBookingToReject(booking);
        setOpenHazardousRejectDialog(true);
      }
    },
    [setHazardousBookingToReject, setOpenHazardousRejectDialog],
  );

  const actionRender = (params) => {
    return (
      <BookingMenuAction
        params={params}
        handlers={{
          deleteEquipment,
          setRowEquipmentId,
          editBooking,
          setPopupEnableStatus,
          rejectEquipment,
          setRejectRowEquipmentId,
          refreshGrid,
          handleApprove,
          handleReject,
        }}
      />
    );
  };

  const overrideCellRender = (key, defaultObj) => {
    switch (key) {
      case "type":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) =>
            item?.data?.type ? <BookingTypeChip key={item.data.id} item={item.data} /> : EN_DASH,
          filterValueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
          valueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
        };
        break;

      case "hazardous":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) => <BookingHazardousChip key={item.data.id} item={item.data} />,
          filterValueGetter: hazardousStatusGetter,
          valueGetter: hazardousStatusGetter,
        };
        break;

      case "dateFrom":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          checkboxSelection: true,
          showDisabledCheckboxes: true,
          filterParams,
          valueFormatter: dateFromFormatter,
          filterValueGetter: dateFromFormatter,
        };
        break;
      case "dateTo":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateToFormatter,
          filterValueGetter: dateToFormatter,
        };
        break;
      case "equipment":
        defaultObj = {
          ...defaultObj,
          valueFormatter: equipmentGetter,
          filterValueGetter: (params) => params.data.bookedEquipments,
        };
        break;
      case "active":
        defaultObj = {
          ...defaultObj,
          valueGetter: statusGetter,
          filterValueGetter: statusGetter,
        };
        break;

      case "reservedForUsers":
        defaultObj = {
          ...defaultObj,
          valueGetter: reservedForGetter,
          filterValueGetter: reservedForFilterFormatterGetter,
        };
        break;
      case "remark":
        defaultObj = {
          ...defaultObj,
          valueGetter: remarkGetter,
          cellRenderer: (item) => (
            <>
              <span id={`attr_remark-${item?.data?.remark}`}>{valueOrEmpty(item?.data?.remark, false, "-")}</span>
            </>
          ),
          filterValueGetter: remarkGetter,
        };
        break;
      case "assistent":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => {
            if (!params?.data?.assistent || !params?.data?.assistent[0]) return "-";
            const assistantId = params.data.assistent[0];
            const assistantUser = users?.find((user) => user.userId === assistantId);
            if (assistantUser) {
              return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
            }
            return assistantId;
          },
          filterValueGetter: (params) => {
            if (!params?.data?.assistent || !params?.data?.assistent[0]) return "-";
            const assistantId = params.data.assistent[0];
            const assistantUser = users?.find((user) => user.userId === assistantId);
            if (assistantUser) {
              return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
            }
            return assistantId;
          },
        };
        break;
      case "assistentReason":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => {
            return params?.data?.assistanceDeclaration?.assistanceReason || "-";
          },
          filterValueGetter: (params) => {
            return params?.data?.assistanceDeclaration?.assistanceReason || "-";
          },
        };
        break;
      case "actionButton":
        defaultObj = {
          ...defaultObj,
          width: 0,
          maxWidth: 50,
          field: "actions",
          headerName: "",
          filter: false,
          pinned: "right",
          lockVisible: true,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
          lockPosition: "right",
          cellClass: "action-render",
          cellRenderer: actionRender,
        };

        break;

      case "detailExpander":
        defaultObj = {
          ...defaultObj,
          cellRenderer: "agGroupCellRenderer",
          pinned: "left",
        };
        break;

      default:
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => valueOrEmpty(params?.data[key], false, "-"),
          filterValueGetter: (params) => valueOrEmpty(params?.data[key], false, "-"),
        };
        break;
    }
    return defaultObj;
  };
  const parentColumnDef = useMemo(() => {
    let defaultObj;
    let colDef = [];

    for (const key in DropBookingMeta.fields) {
      defaultObj = {
        field: key,
        width: DropBookingMeta.fields[key].width,
        maxWidth: DropBookingMeta.fields[key].maxWidth,
        minWidth: DropBookingMeta.fields[key].minWidth,
        headerName: DropBookingMeta.fields[key].text,
        filter: true,
        floatingFilter: showAdvanceFilter,
      };
      defaultObj = overrideCellRender(key, defaultObj);

      colDef.push(defaultObj);
    }
    return colDef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DropBookingMeta.fields, showAdvanceFilter]);

  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api);
      if (onGridApiReady) {
        onGridApiReady(params.api);
      }
    },
    [onGridApiReady],
  );

  const attr = useMemo(
    () => ({
      height: 400,
      columnDefs: parentColumnDef,
      animateRows: true,
      rowExport: false,
      allowContextMenuWithControlKey: true,
      getContextMenuItems: (params) => {
        return [
          "copy",
          "copyWithHeaders",
          "copyWithGroupHeaders",
          "separator",
          {
            name: "Export",
            subMenu: [
              {
                name: "Export Selected Rows",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(parentColumnDef),
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(parentColumnDef),
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
              "separator",
              {
                name: "Export All (Filtered)",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(parentColumnDef),
                        onlySelected: false,
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(parentColumnDef),
                        onlySelected: false,
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
            ],
          },
        ];
      },
      defaultCsvExportParams: createDefaultCsvExportParams({}, {}, user),
      defaultExcelExportParams: createDefaultExcelExportParams({}, {}, user),
      processCellForClipboard: createClipboardCellProcessor({}, user),
      defaultToolPanel: "filters",
      hiddenByDefault: true,
      rowData: tempRowData,
      onGridReady,
    }),
    [parentColumnDef, tempRowData, user, onGridReady],
  );
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      suppressMenu: true,
      sortable: true,
    };
  }, []);

  useEffect(() => {
    if (gridApi && resetAll) {
      //gridRef.current.api.setQuickFilter(searchText);
      setSearchQuery("");
      setSelectedEquipment([]);
      setSelectedDateRange([
        getWeekStartAndEndInISOString("start", user.timezone),
        getWeekStartAndEndInISOString("end", user.timezone),
      ]);
      // gridRef.current.api.setQuickFilter();
      gridRef.current.api.setFilterModel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAll, gridApi]);

  useEffect(() => {
    let tempArray = [];
    if (tempRowData.length > 0) {
      tempArray = [...tempArray, ...tempRowData];
    }
    if (data.length > 0) {
      tempArray = [...tempArray, ...data];
    }
    setTempRowData(tempArray);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDescriptionUpdate = useCallback(async () => {
    dispatchAction({ type: "clearAll" });
  }, [dispatchAction]);

  const handleHazardousMaterialsUpdate = useCallback(async () => {
    dispatchAction({ type: "clearAll" });
  }, [dispatchAction]);

  const handleAssistanceUpdate = useCallback(async () => {
    dispatchAction({ type: "clearAll" });
  }, [dispatchAction]);

  const detailCellRenderer = useMemo(() => {
    return (props) => {
      if (!props?.data) {
        return null;
      }
      return (
        <BookingDetailRenderer
          {...props}
          isAdminContext={true}
          urlAction={urlAction}
          setOpenHazardousRejectDialog={setOpenHazardousRejectDialog}
          setHazardousBookingToReject={setHazardousBookingToReject}
          setOpenAssistanceDialog={setOpenAssistanceDialog}
          setBookingForAssistance={setBookingForAssistance}
          setIsEditMode={setIsEditMode}
          allowAssistanceEdit={true}
          onDescriptionUpdate={handleDescriptionUpdate}
          onHazardousMaterialsUpdate={handleHazardousMaterialsUpdate}
          onAssistanceUpdateComplete={handleAssistanceUpdate}
          status={status}
          setStatus={setStatus}
        />
      );
    };
  }, [
    urlAction,
    setOpenHazardousRejectDialog,
    setHazardousBookingToReject,
    setOpenAssistanceDialog,
    setBookingForAssistance,
    setIsEditMode,
    handleDescriptionUpdate,
    handleAssistanceUpdate,
    handleHazardousMaterialsUpdate,
    status,
    setStatus,
  ]);

  const deleteEquipment = ({ type = "cancel" } = {}) => {
    setIsDialogOpen(true);
    setDropType(type);
    setIsDropBooking(true);
  };

  const rejectEquipment = () => {
    setIsRejectDialogOpen(true);
    setIsRejectBooking(true);
  };

  const agGridOptions = {
    componentsAdded: selectedEquipment?.length,
    detailRowAutoHeight: true,
  };
  const editBooking = async (editData) => {
    const data = cloneDeep(editData);
    const currentTimeString = moment.tz(user.timezone).utc().format();
    dispatchAction({
      type: "fetching",
      payload: true,
    });
    //fetch data from grpahql
    const { items } = await getAllData({
      client,
      query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
      drillData: true,
      fetchPolicy: "no-cache",
      variables: {
        bookingId: data.id,
        nextToken: null,
        filter: { active: { eq: true } },
      },
      dataPath: ["data", "bookingEntriesByBookingId"],
    });

    if (items.length > 0) {
      const keyArray = Object.keys(singleEventSkeleton);
      data["bookingEntries"] = items.map((item) => {
        let tempObj = {};
        keyArray.forEach((key) => {
          tempObj = {
            ...tempObj,
            [key]: item[key] ?? null,
          };
        });
        //extra keys
        tempObj = {
          ...tempObj,
          inventoryId: item.resource,
          equipment: item.equipment,
          title: item.type,
          description: item?.description,
          elnId: item?.elnId,
          project: item?.project,
          redPandaProjects: item?.redPandaProjects,
          createdBy: item?.createdBy,
          reservedForEmails: item?.reservedForEmails,
        };
        return tempObj;
      });
      if (items[0].elnId) data.elnId = items[0].elnId;
    }

    bookingDispatchAction({
      type: "editBookingData",
      payload: {
        isEditMode: true,
        isRedirectToAdmin: true,
        editBookingData: data,
        isOngoing: currentTimeString > data.dateFrom && currentTimeString < data.dateTo,
      },
    });

    const tempBookingEntries = data?.bookingEntries.map((item) => {
      return {
        ...item,
        inventoryId: item.resource,
        isEditEventFlag: true,
        isOngoingEventFlag:
          (currentTimeString > item.end && currentTimeString > item.start) ||
          (currentTimeString > item.start && currentTimeString < item.end),
        editable: currentTimeString < item.end,
        title: item?.type,
      };
    });

    loadPendingReservations(tempBookingEntries);
    if (data) history.push("/");
  };

  const onRowSelected = useCallback(
    (params) => {
      if (params?.source === "checkboxSelected" || params?.source === "rowClicked") {
        const id = params?.data?.id;
        if (!selectedEquipment.includes(id)) {
          //selected scenario
          setSelectedEquipment((prevState) => {
            return onlyUnique([...prevState, id]);
          });
        } else {
          // un-selected
          let uniqueList = onlyUnique(selectedEquipment);
          const index = uniqueList.indexOf(id);
          if (index > -1) {
            let temp = uniqueList;
            temp.splice(index, 1);
            setSelectedEquipment(() => {
              return temp;
            });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedEquipment],
  );
  return (
    <>
      <BackdropPortal show={fetching} />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1",
          minHeight: "0",
        }}
      >
        <div
          className="grid-container"
          style={{
            width: "100%",
            flex: "1",
            minHeight: "0",
          }}
        >
          <DLabGrid
            {...attr}
            height="100%"
            pagination={true}
            suppressPaginationPanel={true}
            rowPerPage={rowPerPage}
            setRowPerPage={setRowPerPage}
            totalRows={tempRowData?.length ?? 0}
            defaultColDef={defaultColDef}
            masterDetail={true}
            rowSelection={"multiple"}
            suppressRowClickSelection={true}
            detailCellRenderer={detailCellRenderer}
            detailRowAutoHeight={true}
            isRowSelectable={isRowSelectable}
            autoGroupColumnDef={"autoGroupColumnDef"}
            onGridReady={onGridReady}
            gridRef={gridRef}
            onRowSelected={onRowSelected}
            rowStyle={rowStyle}
            options={agGridOptions}
            onFilterChanged={(params) => {
              const filters = params.api.getFilterModel();
              if (Object.keys(filters).length > 0) {
                setDisableResetAll(false);
              }
            }}
          />
        </div>

        <BookingsPaginationActions
          selectedCount={selectedEquipment?.length}
          onCancelClick={() => setOpenMultiDeleteDialog(true)}
        />
      </div>
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo,
)(BookingList);
